<template>
  <!-- eslint-disable max-len -->
  <div>
    <main
      id="main"
      ref="main"
      class="disclaimer-mobile u-container"
    >
      <h1 class="font-bold text-4xl">
        Official USPS<sup>®</sup> Change-of-Address
      </h1>
      <ProgressBar
        :steps="6"
        :current="2"
      />
      <form
        ref="form"
        novalidate
        class="m-auto tablet:w-1/2"
        @submit.prevent="validate(); analytics.form.submitted();"
      >
        <section id="exp-inline-choice" />
        <!-- Mover Type Section -->
        <section class="mt-6">
          <Tooltip
            @show="analytics.click.tooltipMoveType"
          >
            <h2
              id="step-heading"
              class="text-xl"
            >
              Step 2: <span class="font-bold">Who Is Moving?</span>
            </h2>
            <template slot="tip">
              <p>
                <span class="font-bold">Individual:</span> Choose this option if one person is moving. If you receive mail by more than one name (i.e. maiden name or nickname), complete a separate form for each name.
              </p>
              <p class="mt-3">
                <span class="font-bold">Family:</span> Choose this option and complete one form if everyone in your family has the same last name. If not all family members are moving, OR if some family members have different last names, choose the Individual option and submit a separate order for each mover.
              </p>
              <p class="mt-3">
                <span class="font-bold">Business:</span> Choose this option if your business is moving. Individual or Family orders that included a business address as the old address will not be processed. Existing or former employees cannot have their "personal" mail forwarded from a business.
              </p>
            </template>
            <template slot="accessible">
              Mover types description
            </template>
          </Tooltip>
          <div class="text-center mt-6">
            <InputRadio
              ref="individual"
              v-model="moverType"
              form-value="INDIVIDUAL"
              name="move-type"
              class="mb-6"
              button
              :disabled="ivsProofingCompleted || disableNonBusinessMover"
              @input="analytics.click.moverType('INDIVIDUAL');"
            >
              Individual
            </InputRadio>
            <InputRadio
              ref="family"
              v-model="moverType"
              form-value="FAMILY"
              name="move-type"
              class="mb-6"
              button
              :disabled="ivsProofingCompleted || disableNonBusinessMover"
              @input="analytics.click.moverType('FAMILY');"
            >
              Family
            </InputRadio>
            <InputRadio
              ref="business"
              v-model="moverType"
              form-value="BUSINESS"
              name="move-type"
              button
              :disabled="ivsProofingCompleted"
              @input="analytics.click.moverType('BUSINESS');"
            >
              Business
            </InputRadio>
            <p
              v-if="errors.moverType.hasError"
              class="errors text-red-200 text-sm font-bold"
            >
              {{ errors.moverType.message }}
            </p>
          </div>
          <div
            v-if="moverType === 'INDIVIDUAL'"
            class="mt-6 flex flex-row"
            role="group"
            aria-labelledby="individual-help-text"
          >
            <p
              id="individual-help-text"
              class="flex-auto c-input-label"
            >
              Only one person is moving. If you receive mail by more than one name (maiden name, married name or nickname) complete a separate form for each name.
            </p>
          </div>
          <template v-if="moverType === 'FAMILY'">
            <div
              class="mt-6 flex flex-row"
              role="group"
              aria-labelledby="family-same-last-name"
            >
              <p
                id="family-same-last-name"
                class="flex-auto c-input-label mr-1"
              >
                Do all individuals moving from this address share the same last name?
              </p>
              <InputRadio
                v-model="familySameLastName"
                form-value="YES"
                name="family-same-last-name"
                class="flex-shrink"
                :disabled="ivsProofingCompleted"
                @input="familySameLastNameHandle('YES'); analytics.input.familySameLastName('YES')"
              >
                Yes
              </InputRadio>
              <InputRadio
                v-model="familySameLastName"
                form-value="NO"
                name="family-same-last-name"
                class="flex-shrink"
                :disabled="ivsProofingCompleted"
                @input="familySameLastNameHandle('NO'); analytics.input.familySameLastName('NO')"
              >
                No
              </InputRadio>
            </div>
            <p
              :class="{ 'errors': errors.familySameLastName.hasError }"
              class="c-input-helper"
            >
              {{ errors.familySameLastName.message }}
            </p>
            <div
              class="mt-3 flex flex-row"
              role="group"
              aria-labelledby="family-remaining"
            >
              <p
                id="family-remaining"
                class="flex-auto c-input-label mr-1"
              >
                Is anyone with this same last name remaining at the address?
              </p>
              <InputRadio
                v-model="familyRemaining"
                form-value="YES"
                name="family-remaining"
                class="flex-shrink"
                :disabled="ivsProofingCompleted"
                @input="familyRemainingHandle('YES'); analytics.input.familyRemaining('YES')"
              >
                Yes
              </InputRadio>
              <InputRadio
                v-model="familyRemaining"
                form-value="NO"
                name="family-remaining"
                class="flex-shrink"
                :disabled="ivsProofingCompleted"
                @input="familyRemainingHandle('NO'); analytics.input.familyRemaining('NO')"
              >
                No
              </InputRadio>
            </div>
            <p
              :class="{ 'errors': errors.familyRemaining.hasError }"
              class="c-input-helper"
            >
              {{ errors.familyRemaining.message }}
            </p>
            <p>
              <strong>Note: Filing a Family Change-of-Address means EVERYONE in the household with the same last name will have their mail forwarded to the address specified.</strong>
            </p>
          </template>
          <template v-if="moverType === 'BUSINESS'">
            <div
              class="mt-6 flex flex-row"
              role="group"
              aria-labelledby="business-wholeCompany"
            >
              <p
                id="business-wholeCompany"
                class="flex-auto c-input-label"
              >
                Is the ENTIRE business moving?
              </p>
              <InputRadio
                v-model="businessWholeCompany"
                form-value="YES"
                name="business-whole-company"
                class="flex-shrink"
                @input="businessWholeCompanyHandle('YES'); analytics.input.businessWholeCompany('YES')"
              >
                Yes
              </InputRadio>
              <InputRadio
                v-model="businessWholeCompany"
                form-value="NO"
                name="business-whole-company"
                class="flex-shrink"
                :disabled="disableNonBusinessMover"
                @input="businessWholeCompanyHandle('NO'); analytics.input.businessWholeCompany('NO')"
              >
                No
              </InputRadio>
            </div>
            <p
              :class="{ 'errors': errors.businessWholeCompany.hasError }"
              class="c-input-helper"
            >
              {{ errors.businessWholeCompany.message }}
            </p>
          </template>
          <template v-if="enhancedBusinessFlowEnabled && moverType === 'BUSINESS'">
            <fieldset
              ref="multipleAddresses"
              aria-labelledby="multiple-addresses"
              role="group"
              class="flex flex-row"
            >
              <legend class="sr-only">
                Does the business have multiple addresses?
              </legend>
              <p
                id="multiple-addresses"
                class="flex-auto c-input-label"
              >
                Does the business have multiple addresses?
              </p>
              <InputRadio
                v-model="multipleAddresses"
                form-value="YES"
                name="multiple-addresses"
                class="flex-shrink"
                :has-error="errors.multipleAddresses.hasError"
                @input="analytics.input.businessMultipleAddresses('YES')"
              >
                Yes
              </InputRadio>
              <InputRadio
                v-model="multipleAddresses"
                form-value="NO"
                name="multiple-addresses"
                class="flex-shrink"
                :disabled="disableNonBusinessMover"
                :has-error="errors.multipleAddresses.hasError"
                @input="analytics.input.businessMultipleAddresses('NO')"
              >
                No
              </InputRadio>
            </fieldset>
            <p
              :class="{ 'errors': errors.multipleAddresses.hasError }"
              class="c-input-helper"
            >
              {{ errors.multipleAddresses && errors.multipleAddresses.message }}
            </p>
            <p>
              <strong>Note: All mail sent to the business name entered below will be forwarded to the address specified.</strong>
            </p>
          </template>
        </section>
        <div class="c-hairline mt-6" />
        <!-- Mover Information Section -->
        <section class="mt-6">
          <Tooltip
            @show="analytics.click.tooltipInformation"
          >
            <h3 class="font-bold text-xl">
              What's your contact information?
            </h3>
            <template
              v-if="moverType === 'BUSINESS'"
              slot="tip"
            >
              <div v-if="multipleAddresses !== 'YES'">
                <p>
                  Enter the primary business name in this field.
                  <template v-if="flagBusinessAliasEnabled">
                    If you receive mail by more than one business name, please include each business alias name in the expandable fields.
                  </template>
                </p>
                <p
                  v-if="flagBusinessAliasEnabled"
                  class="mt-3"
                >
                  Each alias name added will be treated as its own Change-of-Address order. Only one email address will be collected and applied to all primary and alias names.
                </p>
              </div>
              <div v-else>
                <p>
                  The email address and phone number of the "Primary" business are applied to all businesses listed under saved businesses.
                </p>
              </div>
            </template>
            <template
              v-else
              slot="tip"
            >
              <p>
                <span class="font-bold">First Name (or Initial):</span> Enter your first name or initial.
              </p>
              <p class="mt-3">
                <span class="font-bold">Middle Name (or Initial):</span> Enter your middle name or initial.
              </p>
              <p class="mt-3">
                <span class="font-bold">Last Name:</span> Fill in only one last name. If anyone with the same last name is moving to a different address, use a separate Change-of-Address for each person.
              </p>
            </template>
            <template slot="accessible">
              Contact information description
            </template>
          </Tooltip>
          <!-- Business Specific Inputs -->
          <template v-if="moverType === 'BUSINESS' && multipleAddresses !== 'YES'">
            <InputText
              v-model.trim="businessName"
              label="Business Name"
              placeholder="Business Name"
              name="business-name"
              class="mt-3"
              :has-error="errors.businessName.hasError"
              @blur="analytics.input.businessName"
              @input="errors.businessName.hasError = false"
            >
              <span
                v-if="errors.businessName.hasError"
                slot="helper"
              >
                {{ errors.businessName.message }}
              </span>
            </InputText>
            <template v-if="flagBusinessAliasEnabled && showBusinessAlias">
              <div class="c-hairline mt-5 mb-5" />
              <BusinessAlias
                v-if="enhancedBusinessAliasesEnabled"
                ref="businessAliases"
                :value="businessAliases"
                :field-error="fieldErrors"
                :analytics="analytics"
                :business-name="businessName"
                :initial-alias-count="enhancedBusinessAliasesInitialCount"
                :aliases-increment="enhancedBusinessAliasesIncrementCount"
                :max-alias-count="enhancedBusinessAliasesMaxCount"
              />


              <template v-if="!enhancedBusinessAliasesEnabled">
                <InputText
                  v-model.trim="businessAlias1"
                  label="Business Alias 1"
                  placeholder="Business Alias 1"
                  name="business-alias-1"
                  class="mt-3"
                  :has-error="errors.businessAlias1.hasError"
                  @blur="analytics.input.businessAlias('1')"
                >
                  <span
                    v-if="errors.businessAlias1.hasError"
                    slot="helper"
                  >
                    {{ errors.businessAlias1.message }}
                  </span>
                </InputText>
                <InputText
                  v-model.trim="businessAlias2"
                  label="Business Alias 2"
                  placeholder="Business Alias 2"
                  name="business-alias-2"
                  class="mt-3"
                  :has-error="errors.businessAlias2.hasError"
                  @blur="analytics.input.businessAlias('2')"
                >
                  <span
                    v-if="errors.businessAlias2.hasError"
                    slot="helper"
                  >
                    {{ errors.businessAlias2.message }}
                  </span>
                </InputText>
                <InputText
                  v-model.trim="businessAlias3"
                  label="Business Alias 3"
                  placeholder="Business Alias 3"
                  name="business-alias-3"
                  class="mt-3"
                  :has-error="errors.businessAlias3.hasError"
                  @blur="analytics.input.businessAlias('3')"
                >
                  <span
                    v-if="errors.businessAlias3.hasError"
                    slot="helper"
                  >
                    {{ errors.businessAlias3.message }}
                  </span>
                </InputText>
                <InputText
                  v-model.trim="businessAlias4"
                  label="Business Alias 4"
                  placeholder="Business Alias 4"
                  name="business-alias-4"
                  class="mt-3"
                  :has-error="errors.businessAlias4.hasError"
                  @blur="analytics.input.businessAlias('4')"
                >
                  <span
                    v-if="errors.businessAlias4.hasError"
                    slot="helper"
                  >
                    {{ errors.businessAlias4.message }}
                  </span>
                </InputText>
              </template>
            </template>
            <button
              v-if="flagBusinessAliasEnabled"
              id="biz-alias-button"
              type="button"
              class="text-blue-100 font-bold underline mt-3"
              @click="showBusinessAlias = !showBusinessAlias; analytics.click.businessAliases(showBusinessAlias);"
            >
              <template v-if="showBusinessAlias">
                My business does not go by another name
              </template>
              <template v-else>
                My business also goes by another name
              </template>
            </button>
            <button
              v-if="showBusinessAlias && enhancedBusinessAliasesEnabled"
              id="biz-more-alias-button"
              type="button"
              class="biz-more-alias-button text-blue-100 font-bold underline mt-5"
              @click.prevent="moreAlias()"
            >
              <SvgPlusIcon
                color="#3573b1"
                :height="12"
                :width="12"
              /><span>Add more aliases</span>
            </button>
          </template>
          <template v-if="moverType !== 'BUSINESS'">
            <InputText
              ref="firstName"
              v-model.trim="firstName"
              label="First Name"
              placeholder="First Name"
              name="first-name"
              class="mt-3"
              :has-error="errors.firstName.hasError"
              :disabled="ivsProofingCompleted"
              @blur="analytics.input.firstName"
              @input="errors.firstName.hasError = false"
            >
              <span
                v-if="errors.firstName.hasError"
                slot="helper"
              >
                {{ errors.firstName.message }}
              </span>
            </InputText>
            <InputText
              v-model="middleName"
              label="Middle Name or Initial"
              placeholder="Middle Name or Initial"
              name="middle-initial"
              class="mt-3"
              :has-error="middleName ? errors.middleName.hasError : false"
              :disabled="ivsProofingCompleted"
              @input="middleName ? errors.middleName.hasError = false : null"
            >
              <span
                v-if="middleName && errors.middleName.hasError"
                slot="helper"
              >
                {{ errors.middleName.message }}
              </span>
            </InputText>
            <InputText
              ref="lastName"
              v-model.trim="lastName"
              label="Last Name"
              placeholder="Last Name"
              name="last-name"
              class="mt-3"
              :has-error="errors.lastName.hasError"
              :disabled="ivsProofingCompleted"
              @blur="analytics.input.lastName"
              @input="errors.lastName.hasError = false"
            >
              <span
                v-if="errors.lastName.hasError"
                slot="helper"
              >
                {{ errors.lastName.message }}
              </span>
            </InputText>
            <InputSuffix
              v-model="suffix"
              label="Suffix"
              name="suffix"
              class="mt-3"
              :disabled="ivsProofingCompleted"
            />
          </template>
          <InputText
            ref="email"
            v-model.trim="email"
            label="Email Address"
            placeholder="Email Address"
            name="email"
            class="mt-3"
            :has-error="errors.email.hasError"
            :disabled="ivsProofingCompleted || !primaryBusiness"
            @blur="analytics.input.email"
            @input="errors.email.hasError = false"
          >
            <span
              v-if="errors.email.hasError"
              slot="helper"
            >
              {{ errors.email.message }}
            </span>
          </InputText>
          <div class="mt-5">
            <Tooltip class="cb-tooltip">
              <label class="reminder_email--label">
                <span>
                  Check this box to opt-in to receive a one-time reminder email.
                </span>
                <input
                  id="optin-mail-reminder"
                  :checked="optInMailReminder"
                  class="mt-3"
                  type="checkbox"
                  @click="optInMailReminder = !optInMailReminder; analytics.click.optInReminder(optInMailReminder)"
                >
                <span class="checkmark" />
              </label>
              <template slot="tip">
                <p>
                  By opting in, if you choose not to complete your Change-of-Address
                  (COA), USPS will send you a one-time email reminder to come back
                  and finalize your request. Your COA will not be activated until
                  it is complete.
                </p>
              </template>
            </Tooltip>
          </div>
          <InputPhone
            ref="phone"
            v-model="phoneNumber"
            :label="phoneNumLabel"
            placeholder="(000) 000-0000"
            name="phone"
            class="mt-3"
            :has-error="errors.phoneNumber.hasError"
            :disabled="ivsProofingCompleted || !primaryBusiness"
            @blur="analytics.input.phone"
            @input="errors.phoneNumber.hasError = false"
          >
            <span
              v-if="errors.phoneNumber.hasError"
              slot="helper"
            >
              {{ errors.phoneNumber.message }}
            </span>
          </InputPhone>
          <div
            v-if="twoFactorAuthEnabled"
            class="mt-3"
          >
            <p>
              A valid mobile phone number is now required to process your online Change-of-Address (COA) request. Your mobile phone number will be used to complete online Identity Proofing and Verification, in an effort to improve the security of the online COA process, and to identify and mitigate potential fraud. Online Identity Proofing may include a Phone Verification and One-Time Passcode solution and/or a Device Reputation Remote Identity Verification solution, as applicable.
            </p>
          </div>
          <InputDropdown
            v-if="!twoFactorAuthEnabled"
            ref="phoneType"
            v-model="phoneType"
            label="Phone Type"
            name="phone-type"
            class="mt-3"
            :disabled="!primaryBusiness"
            :has-error="errors.phoneType.hasError"
            @input="analytics.input.phoneType"
          >
            <option
              value="MOBILE"
            >
              Mobile
            </option>
            <option value="HOME">
              Home
            </option>
            <span
              v-if="errors.phoneType.hasError"
              slot="helper"
            >
              {{ errors.phoneType.message }}
            </span>
          </InputDropdown>
        </section>
        <div class="c-hairline mt-6" />
        <!-- Forward Type Section -->
        <section class="mt-6">
          <Tooltip
            @show="analytics.click.tooltipForwardType"
          >
            <h3 class="font-bold text-xl">
              Type of Move
            </h3>
            <div slot="tip">
              <p>
                If you plan to move to a new address for less than 6 months and then to a different address (not your old address), choose "Permanent" and fill out another Change-of-Address request at that time.
              </p>
              <p
                v-if="multipleAddresses === 'YES'"
                class="mt-5"
              >
                The forwarding type applies to all businesses listed under saved businesses. You will have an opportunity to change the forwarding type for each business on the verify-info page.
              </p>
            </div>
            <template slot="accessible">
              Permanent versus temporary description
            </template>
          </Tooltip>
          <div class="text-center mt-6">
            <InputRadio
              ref="permanent"
              v-model="forwardType"
              form-value="PERMANENT"
              class="mb-6"
              name="forward-type"
              button
              :disabled="!primaryBusiness"
              @input="analytics.click.forwardType('PERMANENT');"
            >
              Permanent
            </InputRadio>
            <InputRadio
              ref="temporary"
              v-model="forwardType"
              form-value="TEMPORARY"
              name="forward-type"
              button
              :disabled="!primaryBusiness"
              @input="analytics.click.forwardType('TEMPORARY');"
            >
              Temporary
            </InputRadio>
          </div>
          <p
            v-if="errors.forwardType.hasError"
            class="errors text-red-200 text-sm font-bold text-center"
          >
            {{ errors.forwardType.message }}
          </p>
          <p class="mt-6">
            <span class="font-bold">Select Permanent</span> if you don't plan to move back to your old address (most moves are permanent).
          </p>
          <p class="mt-3">
            <span class="font-bold">Select Temporary</span> if you plan to move back to your old address in less than six months.
          </p>
        </section>
        <div class="c-hairline mt-6" />
        <!-- Move Date Section -->
        <section class="mt-6">
          <Tooltip
            @show="analytics.click.tooltipForwardStartDate"
          >
            <h3 class="font-bold text-xl">
              When should we start forwarding mail to your new address?
            </h3>
            <div slot="tip">
              <p>
                Enter the date you'd like us to begin forwarding your mail to your new address. The date you enter must not be more than 30 days prior to today, or more than 3 months from today. Mail should arrive at your new address 7-10 Postal business days after your move effective date OR your file date, whichever date is later.
              </p>
              <p
                v-if="multipleAddresses === 'YES'"
                class="mt-5"
              >
                The forwarding start <span v-if="forwardType !== 'PERMANENT'">and end</span> date applies to businesses listed under saved businesses. You will have an opportunity to change the forwarding start <span v-if="forwardType !== 'PERMANENT'">and end</span> date for each business on the verify-info page.
              </p>
            </div>
            <template slot="accessible">
              Forwarding dates description
            </template>
          </Tooltip>
          <InputDatePicker
            ref="startDate"
            v-model="startDate"
            :label="forwardType !== 'TEMPORARY' ? 'Mail Forwarding Date' : 'Start Forwarding'"
            :min="minStartDate"
            :max="maxStartDate"
            name="forward-start-date"
            class="mt-3"
            :disabled="!primaryBusiness"
            :has-error="errors.startDate.hasError"
            @input="analytics.input.startDate"
          >
            <span slot="helper">
              {{ errors.startDate.message }}
            </span>
          </InputDatePicker>
          <InputDatePicker
            v-if="forwardType === 'TEMPORARY'"
            v-model="stopDate"
            :min="minStopDate"
            :max="maxStopDate"
            :open-date="minStopDate"
            label="End Forwarding"
            name="forward-stop-date"
            class="mt-3"
            :disabled="!primaryBusiness"
            :has-error="errors.stopDate.hasError"
            @input="analytics.input.stopDate"
          >
            <template #rules>
              <p class="mb-4">
                The minimum length for filing a temporary Change-of-Address is 15 days.
              </p>
              <p>
                The initial forwarding period is restricted to 6 months, but can be extended up to 12 months.
              </p>
            </template>
            <span slot="helper">
              {{ errors.stopDate.message }}
            </span>
          </InputDatePicker>
        </section>
        <div class="c-hairline mt-6" />
        <template v-if="multipleAddresses === 'YES'">
          <MultipleBusinessAddresses
            ref="multipleAddressesComponent"
            :forward-type-val="forwardType"
            :is-simplify="simplify"
            :start-date-val="startDate"
            :stop-date-val="stopDate"
            :business-whole-company-val="businessWholeCompany"
            :email-val="email"
            :phone-number-val="phoneNumber"
            :phone-type-val="phoneType"
            :opt-in-mail-reminder-val="optInMailReminder"
            view-mode="control"
            this.errors
            @primary="setPrimaryBusiness"
            @validateForm="validateForm()"
            @fieldErrors="(fieldErrors)=>updateFieldErrors(fieldErrors)"
            @fieldErrorsMilitary="updateFieldErrorsMilitary"
            @disableNonBusinessMovers="disableMoverType"
            @updateForwardFields="updateForwardingFields"
          />
        </template>
        <!-- Old Address Section -->
        <div v-else>
          <section class="mt-6">
            <Tooltip
              @show="analytics.click.tooltipOldAddress"
            >
              <h3 class="font-bold text-xl">
                What's your old address?
              </h3>
              <template slot="tip">
                <p>
                  This is the address you're moving from. Enter a valid address, and remember to include apartment/suite number if applicable.
                </p>
                <p class="mt-3">
                  If your address is a PO Box, enter it in the Street Address field, for example, PO Box 1001. Please note that Change-of-Address request from non-USPS Private Mail Boxes (PMB) cannot be processed.
                </p>
              </template>
              <template slot="accessible">
                Old address description
              </template>
            </Tooltip>
            <InputZipCode
              ref="oldZipCode"
              v-model="oldZipCode"
              label="ZIP Code™"
              placeholder="00000"
              name="old-zip-code"
              class="mt-3"
              :has-error="errors.oldZipCode.hasError"
              :async-fn="zipSearch({
                cityName: oldCity,
                state: oldState,
              })"
              clear
              :transform-compare="zipCompare"
              :transform-label="zipLabel"
              :transform-value="zipValue"
              :disabled="ivsProofingCompleted"
              @blur="analytics.input.oldZipCode"
              @select="oldCity = $event.cityName; oldState = $event.state;"
              @clear="clearContent"
            >
              <template slot="helper">
                {{ errors.oldZipCode.message }}
              </template>
            </InputZipCode>

            <InputListBox
              ref="oldCity"
              v-model="oldCity"
              label="City"
              placeholder="City"
              name="old-city"
              class="mt-3"
              clear
              :has-error="errors.oldCity.hasError"
              :async-fn="citySearch({
                zip: oldZipCode,
                state: oldState,
              })"
              :transform-compare="zipCompare"
              :transform-label="cityLabel"
              :transform-value="cityValue"
              :disabled="ivsProofingCompleted"
              @blur="analytics.input.oldCity"
              @select="oldZipCode = $event.zip; oldState = $event.state;"
              @clear="clearContent"
            >
              {{ errors.oldCity.message }}
            </InputListBox>
            <InputState
              ref="oldState"
              v-model="oldState"
              label="State"
              name="old-state"
              class="mt-3"
              :has-error="errors.oldState.hasError"
              :disabled="ivsProofingCompleted"
              @input="analytics.input.oldState"
            >
              <span
                v-if="errors.oldState.hasError"
                slot="helper"
              >
                {{ errors.oldState.message }}
              </span>
            </InputState>
            <InputListBox
              ref="oldStreet"
              v-model="oldStreetAddress"
              label="Street Address"
              placeholder="Street Address"
              name="old-street-address"
              class="mt-3"
              clear
              :has-error="errors.oldStreetAddress.hasError && !showOldPoBox"
              :async-fn="streetSearch({
                zipcode: oldZipCode,
                cityName: oldCity,
                state: oldState,
              })"
              :transform-compare="streetCompare"
              :transform-label="streetLabel"
              :transform-value="streetValue"
              :disabled="ivsProofingCompleted"
              @input="oldPoBoxNumber = ''"
              @blur="analytics.input.oldStreet"
              @select="oldZipCode = $event.zip;
                       oldState = $event.state;
                       oldCity = $event.cityName;"
              @clear="clearContent"
            >
              {{ errors.oldStreetAddress.message }}
            </InputListBox>
            <InputText
              v-if="oldState === 'PR'"
              ref="oldUrbanizationName"
              v-model="oldUrbanizationName"
              label="Urbanization Name (Puerto Rico only)"
              name="old-urbanization-name"
              placeholder="URB HIGHLAND GDNS"
              class="mt-5"
              clear
              :has-error="errors.oldUrbanizationName.hasError"
              :disabled="ivsProofingCompleted"
              @blur="analytics.input.oldUrbanizationName"
              @clear="clearContent"
            >
              <span slot="helper">
                {{ errors.oldUrbanizationName.message }}
              </span>
            </InputText>
            <InputText
              v-if="showOldPoBox"
              ref="oldPoBoxNumber"
              v-model="oldPoBoxNumber"
              name="old-po-box-number"
              placeholder="PO Box 123"
              class="mt-5"
              clear
              :has-error="errors.oldPoBoxNumber.hasError"
              :disabled="ivsProofingCompleted"
              @blur="analytics.input.oldPoBox"
              @input="oldAddressError = ''"
              @select="oldZipCode = $event.zip;
                       oldCity = $event.cityName;
                       oldState = $event.state;"
              @clear="clearContent"
            >
              <Tooltip slot="label">
                <span>Po Box</span>
                <template slot="tip">
                  <p>
                    <span class="font-bold">Note:</span> The address you provided is not considered a proper mailing address within this ZIP Code™. A proper mailing address within this ZIP Code™ requires a PO Box address. Please input your PO Box number in the “PO Box” field and click "Next" to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
                  </p>
                </template>
                <template slot="accessible">
                  Old address PO box note
                </template>
              </Tooltip>
              <span slot="helper">
                {{ errors.oldPoBoxNumber.message }}
              </span>
            </InputText>
          </section>
          <div class="c-hairline mt-6" />
          <!-- New Address Section -->
          <section class="mt-6">
            <Tooltip
              @show="analytics.click.tooltipNewAddress"
            >
              <h3 class="font-bold text-xl">
                What's your new address?
              </h3>
              <template slot="tip">
                <p>
                  This is the address you are moving to. Enter a valid address, and remember to include apartment/suite number if applicable.
                </p>
                <p class="mt-3">
                  If your address is a PO Box, enter it in the Street Address field, for example, PO Box 1001. Please note that Change-of-Address request from non-USPS Private Mail Boxes (PMB) cannot be processed.
                </p>
              </template>
              <template slot="accessible">
                New address description
              </template>
            </Tooltip>
            <InputZipCode
              ref="newZipCode"
              v-model="newZipCode"
              label="ZIP Code™"
              placeholder="00000"
              name="new-zip-code"
              class="mt-3"
              clear
              :has-error="errors.newZipCode.hasError"
              :async-fn="zipSearch({
                cityName: newCity,
                state: newState,
              })"
              :transform-compare="zipCompare"
              :transform-label="zipLabel"
              :transform-value="zipValue"
              @blur="analytics.input.newZipCode"
              @select="newCity = $event.cityName; newState = $event.state;"
              @clear="clearContent"
            >
              <template slot="helper">
                {{ errors.newZipCode.message }}
              </template>
            </InputZipCode>
            <InputListBox
              ref="newCity"
              v-model="newCity"
              label="City"
              placeholder="City"
              name="new-city"
              class="mt-3"
              clear
              :has-error="errors.newCity.hasError"
              :async-fn="citySearch({
                zip: newZipCode,
                state: newState,
              })"
              :transform-compare="zipCompare"
              :transform-label="cityLabel"
              :transform-value="cityValue"
              @blur="analytics.input.newCity"
              @select="newZipCode = $event.zip; newState = $event.state;"
              @clear="clearContent"
            >
              {{ errors.newCity.message }}
            </InputListBox>
            <InputState
              ref="newState"
              v-model="newState"
              label="State"
              name="new-state"
              class="mt-3"
              :has-error="errors.newState.hasError"
              @input="analytics.input.newState"
            >
              <span
                v-if="errors.newState.hasError"
                slot="helper"
              >
                {{ errors.newState.message }}
              </span>
            </InputState>
            <InputListBox
              ref="newStreet"
              v-model="newStreetAddress"
              label="Street Address"
              placeholder="Street Address"
              name="new-street-address"
              class="mt-3"
              clear
              :has-error="errors.newStreetAddress.hasError && !showNewPoBox && !showNewPrivateMailbox"
              :async-fn="streetSearch({
                cityName: newCity,
                zipcode: newZipCode,
                state: newState,
              })"
              :transform-compare="streetCompare"
              :transform-label="streetLabel"
              :transform-value="streetValue"
              @input="newPoBoxNumber = ''; newPrivateMailbox = '';"
              @blur="analytics.input.newStreet"
              @select="newZipCode = $event.zip;
                       newCity = $event.cityName;
                       newState = $event.state;"
              @clear="clearContent"
            >
              {{ errors.newStreetAddress.message }}
            </InputListBox>
            <InputText
              v-if="newState === 'PR'"
              ref="newUrbanizationName"
              v-model="newUrbanizationName"
              label="Urbanization Name (Puerto Rico only)"
              name="new-urbanization-name"
              placeholder="URB HIGHLAND GDNS"
              class="mt-5"
              clear
              :has-error="errors.newUrbanizationName.hasError"
              @blur="analytics.input.newUrbanizationName"
              @clear="clearContent"
            >
              <span slot="helper">
                {{ errors.newUrbanizationName.message }}
              </span>
            </InputText>
            <InputText
              v-if="showNewPoBox"
              ref="newPoBoxNumber"
              v-model="newPoBoxNumber"
              name="new-po-box-number"
              placeholder="PO Box 123"
              class="mt-3"
              clear
              :has-error="errors.newPoBoxNumber.hasError"
              @blur="analytics.input.newPoBox"
              @clear="clearContent"
            >
              <Tooltip slot="label">
                <span>Po Box</span>
                <template slot="tip">
                  <p>
                    <span class="font-bold">Note:</span> The address you provided is not considered a proper mailing address within this ZIP Code™. A proper mailing address within this ZIP Code™ requires a PO Box address. Please input your PO Box number in the “PO Box” field and click "Next" to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
                  </p>
                </template>
                <template slot="accessible">
                  New address PO box note
                </template>
              </Tooltip>
              <template slot="helper">
                {{ errors.newPoBoxNumber.message }}
              </template>
            </InputText>
            <InputText
              v-if="showNewPrivateMailbox"
              v-model="newPrivateMailbox"
              label="Private Mailbox"
              name="new-private-mailbox"
              placeholder="PMB 123"
              class="mt-3"
              clear
              :has-error="errors.newPrivateMailbox.hasError || !newPrivateMailbox"
              @blur="analytics.input.newPrivateMailbox"
              @clear="clearContent"
            >
              <template slot="helper">
                {{ errors.newPrivateMailbox.message }}
              </template>
            </InputText>
          </section>
        </div>
        <!-- Informed Delivery Section -->
        <template v-if="informedDeliveryEligible">
          <div class="c-hairline mt-6" />
          <section class="mt-6">
            <h3 class="font-bold text-xl">
              Informed Delivery<sup>®</sup>
            </h3>
            <p class="mt-3">
              New to Informed Delivery? This free service gives you a digital preview of incoming mail and lets you track and manage package delivery to your new address. New users, check the box to sign up.
            </p>
            <InputCheckbox
              ref="informedDelivery"
              v-model="informedDelivery"
              name="informed-delivery"
              class="mt-3"
              @input="analytics.click.informedDelivery"
            >
              I opt in to Informed Delivery<sup>®</sup> and have read, understand, and agree to the <a
                id="informed-delivery-terms"
                href="https://reg.usps.com/entreg/assets/html/informed-delivery.html"
                target="_blank"
                rel="noopener noreferrer"
                class="c-link"
                @click.stop
              >Terms and Conditions</a>.
            </InputCheckbox>
            <p class="mt-3">
              If you're already using Informed Delivery, don't check the box, it will carry over to your new address.
            </p>
          </section>
        </template>
        <div class="c-hairline mt-6" />
        <!-- Privacy Act Section -->
        <section class="mt-6">
          <div v-if="twoFactorAuthEnabled">
            <p class="font-bold">
              Request for Change-of-Address and Forwarding Services
            </p>
            <p class="mt-3 mb-6">
              <strong>Privacy Act Statement:</strong>  Your information will be used to provide mail forwarding
              and Change-of-Address (COA) services and move related advertisements, to verify your identity,
              to facilitate communication, to facilitate surveys regarding the COA process, to identify and mitigate potential fraud,
              and to provide address correction services. Collection is authorized by 39 U.S.C. 401, 403, 404.
              Providing the information is voluntary,
              but if not supplied, we may not be able to process and fulfill your request for services.
              <button
                v-if="!privacyActFullContent"
                class="font-bold"
                :class="['c-link--choice', 'c-link--inline']"
                type="button"
                @click="togglePrivacyActContent"
              >
                Click here to display the entire Privacy Act Statement
              </button>
              <template v-if="privacyActFullContent">
                We do not disclose your information to third parties without your
                consent, except to facilitate the transaction, to act on your behalf
                or request, or as legally required. This includes the following standard
                and specific routine uses: Incident to legal proceedings; for law enforcement
                purposes; to a congressional office; to agents or contractors; to auditors;
                to labor organizations; to federal, state, local, or foreign government
                agencies; to agencies and entities for financial matters; for customer service
                purposes; to a federal, state, or local government agency upon certification
                when required for the performance of its duties; to a law enforcement agency
                after the Postal Inspection Service has confirmed that the COA information is
                needed for a criminal investigation; for service of process; for purpose of
                jury service; to agencies or entities at customer’s request; to a disaster
                relief organization; pursuant to the order of a court of competent jurisdiction;
                for address correction services only if the mailer is in possession of the name
                and old address; and to USPS licensed service providers to perform mailing list
                address correction services for lists containing your name and old address. For
                a list of licensed service providers please visit <a
                  href="https://postalpro.usps.com/NCOALink_Service_Providers"
                  :class="['c-link--choice', 'c-link--inline']"
                >https://postalpro.usps.com/NCOALink_Service_Providers</a>. For more
                information regarding our privacy policies visit <a
                  href="https://www.usps.com/privacypolicy"
                  target="_blank"
                  :class="['c-link--choice', 'c-link--inline']"
                  rel="noopener noreferrer"
                >www.usps.com/privacypolicy</a>.
                <br>
                <button
                  :class="['c-link--choice', 'c-link--inline']"
                  type="button"
                  @click="togglePrivacyActContent"
                >
                  Display Less
                </button>
              </template>
            </p>
            <p class="font-bold">
              Identity Verification and Online Identity Proofing
            </p>
            <p class="mt-3 mb-6">
              <strong>Privacy Act Statement:</strong> Your information will be used to verify
              your identity using enhanced remote identity proofing when applying for
              Change-of-Address (COA) services online, to improve the security of the COA
              process, and to identify and mitigate potential fraud. Collection is authorized
              by 39 U.S.C. 401, 403, 404, and 411. Providing your information is voluntary,
              but if not provided, we may not be able to verify your identity online when
              applying for COA and mail forwarding services.
              <button
                v-if="!identityProofingFullContent"
                class="font-bold"
                :class="['c-link--choice', 'c-link--inline']"
                type="button"
                @click="toggleIdentityProofingContent"
              >
                Click here to display the entire Privacy Act Statement
              </button>
              <template v-if="identityProofingFullContent">
                We do not disclose your information without your consent to third parties,
                except for the following limited circumstances: incident to legal proceedings
                involving the Postal Service; for law enforcement purposes; to a congressional
                office on your behalf; to agents or contractors when necessary to fulfill a
                business function; to a U.S. Postal Service auditor; to labor organizations as
                required by applicable law; to government agencies in connection with decisions
                as necessary; to agencies and entities for financial matters; and for customer
                service purposes. For more information on our privacy policies visit <a
                  href="https://www.usps.com/privacypolicy"
                  target="_blank"
                  :class="['c-link--choice', 'c-link--inline']"
                  rel="noopener noreferrer"
                >www.usps.com/privacypolicy</a>.
                <br>
                <button
                  :class="['c-link--choice', 'c-link--inline']"
                  type="button"
                  @click="toggleIdentityProofingContent"
                >
                  Display Less
                </button>
              </template>
            </p>
          </div>
          <p
            v-else
            class="mt-3 mb-6"
          >
            <span class="font-bold">Privacy Act Statement:</span> Your information will be used to provide mail forwarding and Change-of-Address (COA) services and move related advertisements, to verify your identity, to facilitate communication, to facilitate surveys regarding the COA process, to identify and mitigate potential fraud, and to provide address correction services. Collection is authorized by 39 U.S.C. 401, 403, 404. Providing the information is voluntary, but if not supplied, we may not be able to process and fulfill your request for services.
            <button
              v-if="hideCoaPrivacyActContent"
              :class="{
                'c-link': !showNonControlExperience,
                'c-link--choice': showNonControlExperience,
                'c-link--inline': showNonControlExperience,
              }"
              style="font-weight: bold;"
              type="button"
              @click="hideCoaPrivacyActContent = false"
            >
              Show More
            </button>
            <template v-else>
              We do not disclose your information to third parties without your consent, except to facilitate the transaction, to act on your behalf or request, or as legally required. This includes the following standard and specific routine uses: Incident to legal proceedings; for law enforcement purposes; to a congressional office; to agents or contractors; to auditors; to labor organizations; to federal, state, local, or foreign government agencies; to agencies and entities for financial matters; for customer service purposes; to a federal, state, or local government agency upon certification when required for the performance of its duties; to a law enforcement agency after the Postal Inspection Service has confirmed that the COA information is needed for a criminal investigation; for service of process; for purpose of jury service; to agencies or entities at customer’s request; to a disaster relief organization; pursuant to the order of a court of competent jurisdiction; for address correction services only if the mailer is in possession of the name and old address; and to USPS licensed service providers to perform mailing list address correction services for lists containing your name and old address. For a list of licensed service providers please visit <a
                href="https://postalpro.usps.com/NCOALink_Service_Providers"
                :class="{
                  'c-link': !showNonControlExperience,
                  'c-link--choice': showNonControlExperience,
                  'c-link--inline': showNonControlExperience,
                }"
              >https://postalpro.usps.com/NCOALink_Service_Providers</a>. For more information regarding our privacy policies visit <a
                href="https://www.usps.com/privacypolicy"
                :class="{
                  'c-link': !showNonControlExperience,
                  'c-link--choice': showNonControlExperience,
                  'c-link--inline': showNonControlExperience,
                }"
                target="_blank"
                rel="noopener noreferrer"
              >www.usps.com/privacypolicy</a>.
              <button
                :class="{
                  'c-link': !showNonControlExperience,
                  'c-link--choice': showNonControlExperience,
                  'c-link--inline': showNonControlExperience,
                }"
                type="button"
                @click="hideCoaPrivacyActContent = true"
              >
                Show Less
              </button>
            </template>
          </p>
        </section>
        <transition
          name="fade"
          appear
        >
          <section
            v-if="renderV3Challenge"
            class="mt-6"
          >
            <p class="mb-4">
              For your security, clicking the box below confirms that this Change-of-Address form is being completed by a person, and not an automated query.
            </p>
            <vue-recaptcha
              id="recaptcha-challenge"
              ref="recaptchaChallenge"
              :sitekey="getRecaptchaV2Key()"
              @click.native="analytics.click.recaptchaVerified"
              @verify="recaptchaChallengeToken=$event;"
              @expired="recaptchaChallengeToken='';"
            />
            <transition
              appear
              name="fade"
            >
              <p
                v-if="errors.recaptchaChallengeToken && errors.recaptchaChallengeToken.hasError"
                class="c-input-text__helper c-input-helper errors"
              >
                {{ errors.recaptchaChallengeToken.message }}
              </p>
            </transition>
          </section>
        </transition>
        <StickyPanel>
          <template slot="buttons">
            <router-link
              :to="{ name: 'disclaimer' }"
              class="c-btn--mobile c-btn--secondary--mobile simplify__nav-button"
              replace
            >
              Back
            </router-link>
            <button
              id="next"
              ref="nextBtn"
              class="c-btn--mobile simplify__nav-button"
              @click="analytics.click.next(); trackFirstEvent('next-btn');"
            >
              Next
            </button>
          </template>
        </StickyPanel>
        <StickyPanel
          nav-back="disclaimer"
          :analytics="analytics"
          :click-fn="trackFirstEvent('next-btn')"
          :mobile="true"
        />
      </form>
    </main>
    <div class="w-screen bg-blue-200">
      <div class="flex items-center u-container--x coac-link coac-link--center">
        <div class="text-xl text-white mr-2">
          <a
            id="view-edit-coac"
            :href="this.$sanitize($options.coacUrl)"
            title="Official USPS Change-of-Address Correct Form"
            class="font-bold"
          >
            Manage your Change-of-Address® on managemymove.com
          </a>
        </div>
        <a
          :href="this.$sanitize($options.coacUrl)"
          title="Official USPS Change-of-Address Correct Form"
          class="not-sr-only"
        >
          <span class="red-arrow" />
        </a>
      </div>
    </div>
    <Modal
      v-model="newConstructionModal"
      close-id="new-constr-close-x"
    >
      <ModalNewConstruction
        :street-address="newStreetAddress"
        :city="newCity"
        :state="newState"
        :zip-code="newZipCode"
        :urbanization-name="newUrbanizationName"
        @reset="newConstructionModal = false"
        @override="newConstructionOverride"
      />
    </Modal>
    <Modal v-model="moverTypeFamilyModal">
      <h4>Based on your responses...</h4>
      <p
        class="mt-3"
      >
        {{
          familySameLastName === 'YES' || familyRemaining === 'YES'
            ? 'A Family move will forward all mail associated with this last name. Based on your response, an Individual Change-of-Address must be done for each person moving to ensure the mail of the person remaining is unaffected by this Change-of-Address.'
            : ''
        }}
        {{
          familySameLastName === 'NO' && familyRemaining !== 'YES'
            ? 'A Family move is only required when more than one person, with the same last name, is moving from the address. Anyone with a different last name must file an Individual Change-of-Address.'
            : ''
        }}
      </p>
      <button
        v-if="familySameLastName === 'YES' || familyRemaining === 'YES'"
        class="c-btn mt-3"
        @click="moverType='INDIVIDUAL'; moverTypeFamilyModal=false;"
      >
        Continue
      </button>
      <button
        v-if="familySameLastName === 'NO' && familyRemaining !== 'YES'"
        class="c-btn mt-3"
        @click="moverTypeFamilyModal=false;"
      >
        Continue
      </button>
    </Modal>
    <Modal v-model="moverTypeBusinessModal">
      <h4>Based on your responses...</h4>
      <p
        class="mt-3"
      >
        A Business move should only be submitted when the entire business is moving. If the entire business is moving to a new address, please select Continue, and change your response to Yes.
      </p>
      <button
        class="c-btn mt-3"
        @click="moverType='INDIVIDUAL'; moverTypeBusinessModal=false;"
      >
        Continue
      </button>
    </Modal>
    <Modal
      ref="emailReminderOptOutModal"
      v-model="emailReminderOptOutModal"
    >
      <h4>One-Time Email Opt-Out</h4>
      <p class="mt-3">
        You've opted-out of the one-time reminder email if you don't complete this Change-of-Address transaction.
      </p>
      <p class="mt-3">
        You will still receive confirmation emails if you complete your transaction.
      </p>
      <button
        class="c-btn mt-3"
        @click="emailReminderOptOutModal=false"
      >
        Close
      </button>
    </Modal>
    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address you've entered. To make changes to your current Change-of-Address, visit
        <a
          class="c-link c-link--inline"
          :href="this.$sanitize($options.coacUrl)"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code, which can be located within your confirmation email and the Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import {
  InputCheckbox,
  InputDatePicker,
  InputDropdown,
  InputListBox,
  InputPhone,
  InputRadio,
  InputText,
  InputState,
  InputSuffix,
  InputZipCode,
  Modal,
  ProgressBar,
  Tooltip,
} from '@mgo/ui/lib/components';
import { mobileAnalytics } from './analytics';
import { identify, track } from '../../analytics/events';
import ModalNewConstruction from '../../components/ModalNewConstruction.vue';

// mixin used in both on Disclaimer/desktop.vue as well as MoveCombined/mobile.vue
import moveCombinedMixin from './mixin';
import MultipleBusinessAddresses from '../../components/MultipleAddresses/mobile.vue';
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import destinationManager from '../../mixins/destinationManager';
import emailReminder from '../../mixins/emailReminder';
import grecaptcha from '../../mixins/grecaptcha';
import moverType from '../../mixins/moverType';
import newConstruction from '../../mixins/newConstruction';
import rapidKey from '../../mixins/rapidKey';
import StickyPanel from '../../components/StickyPanel.vue';
import BusinessAlias from '../../components/BusinessAlias.vue';
import SvgPlusIcon from '../../components/SvgPlusIcon.vue';

export default {
  name: 'MobileMoveCombined',
  components: {
    InputCheckbox,
    InputDatePicker,
    InputDropdown,
    InputListBox,
    InputPhone,
    InputRadio,
    InputState,
    InputSuffix,
    InputText,
    InputZipCode,
    Modal,
    MultipleBusinessAddresses,
    StickyPanel,
    ModalNewConstruction,
    ProgressBar,
    Tooltip,
    VueRecaptcha,
    BusinessAlias,
    SvgPlusIcon,
  },
  mixins: [
    addressViolations,
    datePicker,
    destinationManager,
    emailReminder,
    grecaptcha,
    moveCombinedMixin,
    moverType,
    newConstruction,
    rapidKey,
  ],
  computed: {
    analytics() {
      return mobileAnalytics();
    },
    flagBusinessAliasEnabled() {
      return this.$store.get('flagBusinessAliasEnabled');
    },
  },
  async mounted() {
    this.optInMailReminder = JSON.parse(sessionStorage.getItem('optInMailReminder') ?? false);
    if (this.$store.get('config@twoFactorAuthEnabled') && this.$store.get('mfa@allowProfiling')) {
      this.deviceProfiling();
    }

    if (this.flagAmpersendEnabled) {
      this.$store.commit('ENABLE_LOADER');
      this.$amp.veil(this.$refs.main);
    }

    if (this.$coa.coaUUID) {
      await this.hydrateCOA();
      await Promise.all([
        this.hydrateMover(),
        this.hydrateAddresses(),
      ]);
    } else {
      const { utm_medium: utmMedium } = this.$route.query;
      await this.$coa.coaCreate(utmMedium || '');
      this.$setSession(this.$coa.coaUUID, this.$coa.orderUuid);
      if (this.flagAmpersendEnabled) {
        this.$amp.setAnonymousID(this.$coa.coaUUID);
        this.$amp.identify({
          isVerified: false,
          mgoDeviceType: 'MOBILE',
        });
      }
      identify(this.$coa.coaUUID);
      track('CoaCreated', {
        uuid: this.$coa.coaUUID,
        visitId: this.$coa.visitId,
      });
    }

    if (this.flagAmpersendEnabled) {
      await this.$amp.getUnimpressedExperiences();
      this.$amp.unveil(this.$refs.main);
      this.$store.commit('DISABLE_LOADER');
    }

    this.pageView();
    this.dirty = false;
  },
  methods: {
    clearContent(payload) {
      const toKebobCase = (str) => str.replace(/-./g, (x) => x.toUpperCase()[1]);
      const transformed = toKebobCase(payload);
      this[transformed] = '';
      switch (payload) {
        case toKebobCase('old-street-address'):
          this[toKebobCase('old-po-box-number')] = '';
          break;
        case toKebobCase('new-street-address'):
          this[toKebobCase('new-po-box-number')] = '';
          break;
        default:
          break;
      }
    },
    async submit() {
      this.$store.commit('ENABLE_LOADER');

      if (!this.informedDeliveryEligible) {
        this.informedDelivery = false;
      }

      if (this.twoFactorAuthEnabled) {
        this.phoneType = 'MOBILE';
      }

      const { medUpdated, sdUpdated, xhr } = await this.$coa.coaUpdate({
        moverType: this.moverType,
        forwardType: this.forwardType,
        startDate: this.startDate,
        informedDelivery: this.informedDelivery,
        stopDate: this.forwardType === 'TEMPORARY' ? this.stopDate : null,
        businessName: this.moverType === 'BUSINESS' ? this.businessName : null,
        email: this.email || null,
        famPrompt1ShareLastName: this.familySameLastName
          ? this.familySameLastName === 'YES'
          : null,
        famPrompt2SameLastNameRemaining: this.familyRemaining
          ? this.familyRemaining === 'YES'
          : null,
        bizPromptEntireMoving: this.businessWholeCompany
          ? this.businessWholeCompany === 'YES'
          : null,
        multipleAddresses: this.multipleAddresses === 'YES' ?? false,
      });

      this.attachFieldErrors({ xhr });

      // start/stop date updated due to email domain check
      this.medUpdated = medUpdated;
      this.sdUpdated = sdUpdated;

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          moverType: this.moverType,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });

      let businessAliasPayload = [];
      if (this.showBusinessAlias) {
        businessAliasPayload = this.enhancedBusinessAliasesEnabled
          ? this.businessAliases.map((d) => d.value).filter((v) => v)
          : [
            this.businessAlias1,
            this.businessAlias2,
            this.businessAlias3,
            this.businessAlias4,
          ].filter((v) => v);
      }

      const businessAliasHandle = this.flagBusinessAliasEnabled
        && this.moverType === 'BUSINESS' && this.$coa.businessAliasSave(businessAliasPayload);

      const personHandle = this.$coa.personSave({
        personType: 'MOVER',
        firstName: this.moverType !== 'BUSINESS' ? this.firstName : null,
        middleName: this.moverType !== 'BUSINESS' ? this.middleName : null,
        lastName: this.moverType !== 'BUSINESS' ? this.lastName : null,
        suffix: this.moverType !== 'BUSINESS' ? this.suffix : null,
        email: this.email,
        emailConfirm: this.email,
        [`${this.phoneType.toLowerCase()}Phone`]: this.phoneNumber,
      });

      const [
        personResponse,
        businessAliasResponse,
      ] = await Promise.all([
        personHandle,
        businessAliasHandle,
      ]);

      if (this.optInMailReminder) {
        this.$coa.emailReminderOptIn();
      } else {
        this.$coa.emailReminderOptOut();
      }

      this.attachFieldErrors(personResponse);
      this.attachFieldErrors(businessAliasResponse);
      this.prepareFieldErrors(personResponse);

      const addressResponses = await this.$coa.addressSaveV2({
        addressType: 'OLD',
        streetAddress: this.oldStreetAddress,
        city: this.oldCity,
        state: this.oldState,
        zipCode: this.oldZipCode,
        urbanizationName: this.oldState === 'PR'
          ? this.oldUrbanizationName
          : null,
        privateMailbox: null,
        poBoxNumber: this.oldPoBoxNumber || null,
        recentConstruction: false,
        overrideAms: false,
        moverType: this.moverType,
        forwardType: this.forwardType,
      }, {
        addressType: 'NEW',
        streetAddress: this.newStreetAddress,
        city: this.newCity,
        state: this.newState,
        zipCode: this.newZipCode,
        urbanizationName: this.newState === 'PR'
          ? this.newUrbanizationName
          : null,
        privateMailbox: this.newPrivateMailbox || null,
        poBoxNumber: this.newPoBoxNumber || null,
        recentConstruction: this.recentConstruction,
        overrideAms: this.overrideAms,
        moverType: this.moverType,
        forwardType: this.forwardType,
      });

      if (addressResponses?.xhr?.exception) {
        if (addressResponses?.xhr?.exception === 'MilitaryZip5ForwardTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'forwardType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.forwardType.message = addressResponses?.xhr?.message || '';
          this.errors.forwardType.hasError = true;
        } else if (addressResponses?.xhr?.exception === 'MilitaryZip5MoverTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'moverType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.moverType.message = addressResponses?.xhr?.message || '';
          this.errors.moverType.hasError = true;
        } else if (addressResponses?.xhr?.addressType === 'NEW') {
          this.newAddressError = addressResponses?.xhr?.exception || '';
          this.errors.newStreetAddress.hasError = true;
          this.attachFieldErrors(this.newAddressError, 'new');
        } else {
          this.oldAddressError = addressResponses?.xhr?.exception || '';
          this.errors.oldStreetAddress.hasError = true;
          this.attachFieldErrors(this.oldAddressError, 'old');
        }
      }
      if (addressResponses?.xhr?.exception === 'InvalidPrimaryPoBoxException' && this.errors?.newPoBoxNumber?.hasError) {
        this.errors.newStreetAddress.hasError = false;
        this.errors.newStreetAddress.message = '';
      }
      if (addressResponses?.xhr?.fieldErrors && /new/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.newAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.newAddressError, 'new');
      }
      if (addressResponses?.xhr?.fieldErrors && /old/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.oldAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.oldAddressError, 'old');
      }
      if (addressResponses?.xhr?.fieldErrors) {
        // eslint-disable-next-line no-unused-expressions
        addressResponses?.xhr?.fieldErrors.forEach((fieldError) => {
          const fieldNameError = fieldError.field?.split('.')[1].charAt(0).toUpperCase() + fieldError.field?.split('.')[1].slice(1);
          const oldAddressField = `old${fieldNameError}`;
          const newAddressField = `new${fieldNameError}`;
          if (fieldError.field.indexOf('newAddress') === 0) {
            this.errors[newAddressField].message = fieldError.message;
            this.errors[newAddressField].hasError = true;
          } else {
            this.errors[oldAddressField].message = fieldError.message;
            this.errors[oldAddressField].hasError = true;
          }
        });
      }
      if (this.oldAddressError === 'OldAndNewAddressMatchException'
         || this.newAddressError === 'OldAndNewAddressMatchException') {
        this.newAddressError = 'OldAndNewAddressMatchException';
        this.oldAddressError = 'OldAndNewAddressMatchException';
      }

      if (addressResponses?.raw?.status >= 400
            && (addressResponses?.raw?.status !== 420 && addressResponses?.raw?.status !== 422)) {
        this.fieldErrors = [
          ...this.fieldErrors,
          { field: 'oldStreetAddress', message: 'We are unable to validate this address at this time, please try again later.' },
          { field: 'newStreetAddress', message: 'We are unable to validate this address at this time, please try again later.' },
        ];
      }

      track(this.moverUUID ? 'PersonUpdated' : 'PersonCreated',
        {
          personType: 'MOVER',
          phoneType: this.phoneType,
        });

      // log the defaultExceptions for old/new address
      if (!addressResponses?.xhr?.fieldErrors) {
        if (this.oldAddressError) {
          this.defaultException(this.oldAddressError);
        }
        if (this.newAddressError) {
          this.defaultException(this.newAddressError);
        }
      }

      const errorPageRedirect = this.evaluateErrorRedirect(addressResponses);
      if (errorPageRedirect) {
        this.$store.commit('DISABLE_LOADER');
        this.$router.push({ name: errorPageRedirect });
        return;
      }

      if (!addressResponses?.NEW?.uuid) {
        this.newConstructionEvaluateAddress(addressResponses);
      }

      if (this.newConstructionAttempts > 0) {
        this.$store.commit('DISABLE_LOADER');
        return;
      }

      // coaCheckHandle after the old/newAddress response
      const coaCheckHandle = await this.$coa.coaCheck();

      // Throw coaCheckModal if 'CoaCheckException'
      if (coaCheckHandle?.error) {
        const { exception } = coaCheckHandle.xhr;
        if (exception === 'CoaCheckException') {
          this.coaCheckModal = true;
          this.defaultException('DUPCOAEXCEPTION');
          this.$store.commit('DISABLE_LOADER');
          return;
        }
      }

      // log the amsStatusCodes on 200 responses for old/new addresses
      if (addressResponses?.addressType
         && this.isAmsMatch(addressResponses?.amsStatusCode)) {
        this.defaultException(addressResponses.amsStatusCode);
      }

      if (this.flagAmpersendEnabled) {
        this.$amp.identify({
          offersOptIn: (JSON.parse(sessionStorage.getItem('mmco')) ?? this.offersChoice) || false,
          voterregOptIn: (JSON.parse(sessionStorage.getItem('mmcvr')) ?? this.autofillChoice) || false,
          mgoMoverType: this.moverType,
          mgoForwardType: this.forwardType,
          mgoOldZip5: this.oldZipCode,
          mgoNewZip5: this.newZipCode,
        });
      }

      // Add Email domain start/stop banner flags to query string if API updated start/stop dates
      this.next({
        ...this.$route.query,
        ...(this.medUpdated && { medUpdated: this.medUpdated }),
        ...(this.sdUpdated && { sdUpdated: this.sdUpdated }),
      }, addressResponses?.xhr?.exception);
    },
  },
};
</script>
